import { Duration } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import { clampPercent } from '../../../hooks/useTimelineDrag';

export const CurrentTimeIndicator = ({ duration, currentTime, onSeek }: { currentTime: Duration; duration: Duration; onSeek: (seek: Duration) => void }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState(0);
    const [virtualPosition, setVirtualPosition] = useState(0);
    const indicatorRef = useRef<HTMLDivElement>(null);
    const clientX = useRef(0);

    const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
        clientX.current = e.clientX;
    };

    useEffect(() => {
        if (!isDragging) {
            return;
        }

        const onMouseMove = (e: MouseEvent) => {
            // The mouseup was missed (e.g. button released outside the window): stop dragging.
            if (e.buttons === 0) {
                setIsDragging(false);
                return;
            }

            e.preventDefault();

            if (!indicatorRef.current?.parentElement) {
                return;
            }

            const x = clientX.current - e.clientX;
            clientX.current = e.clientX;

            const positionChange = (x / indicatorRef.current.parentElement.getBoundingClientRect().width) * 100;
            if (isNaN(positionChange)) return;
            // Clamp so the scrubber can't accumulate off-screen overshoot and feel "stuck" at the edges.
            setVirtualPosition((prev) => clampPercent(prev - positionChange));
        };

        const onMouseUp = (e: MouseEvent) => {
            e.preventDefault();
            setIsDragging(false);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, [isDragging]);

    useEffect(() => {
        if (duration.toMillis() === 0) {
            return;
        }

        if (isDragging) {
            let p = virtualPosition;
            if (p < 0) p = 0;
            if (p > 100) p = 100;

            let seek = Duration.fromMillis(Math.round(((p / 100) * duration.toMillis()) / 1000) * 1000);

            setPosition((seek.toMillis() / duration.toMillis()) * 100);
            onSeek(seek);
            return;
        }
        setPosition((currentTime.toMillis() / duration.toMillis()) * 100);
        setVirtualPosition((currentTime.toMillis() / duration.toMillis()) * 100);
    }, [isDragging, currentTime, duration, virtualPosition]);

    return <div ref={indicatorRef} onMouseDown={onMouseDown} style={{ left: `${position}%` }} className="time-indicator"></div>;
};
